import { Link, useNavigate } from "react-router-dom";
import { Charity } from "../../../../models";
import { updateCharity, State } from "../actions";
import { useActionState, useEffect } from "react";
import FormBody from "../../../../components/form-body";
import Label from "../../../../components/label";
import Input from "../../../../components/input";
import FormError from "../../../../components/form-error";
import Textarea from "../../../../components/textarea";
import { Button } from "../../../../components/button";
import toast from "react-hot-toast";

export default function CharityEditForm({ charity }: { charity: Charity }) {
  const navigate = useNavigate();
  const initialState: State = {
    message: null,
    success: false,
    errors: {},
    defaultValues: {
      name: charity.name || "",
      registrationNo: charity.registrationNo || "",
      income: charity.income || 0,
      expenditure: charity.expenditure || 0,
      website: charity.website || "",
      aimsAndActivities: charity.aimsAndActivities || "",
      whatItDoes: charity.whatItDoes || "",
      whoItHelps: charity.whoItHelps || "",
      howItWorks: charity.howItWorks || "",
    },
  };

  const updateCharityWithId = updateCharity.bind(null, charity.id!);
  const [state, formAction, isPending] = useActionState(
    updateCharityWithId,
    initialState
  );
  useEffect(() => {
    if (state.success) {
      navigate("/admin/charities");
      toast.success("Charity updated successfully");
    }
  }, [state]);

  return (
    <form className="space-y-6 max-w-3xl py-8 text-left" action={formAction}>
      <FormBody>
        <div className="mb-4">
          <Label htmlFor="name">Charity Name</Label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder="Enter charity name"
            aria-describedby="name-error"
            defaultValue={state.defaultValues.name ?? ""}
            required
          />
          <FormError id="name-error" message={state.errors?.name} />
        </div>
        
        <div className="mb-4">
          <Label htmlFor="registrationNo">Registration Number</Label>
          <Input
            id="registrationNo"
            name="registrationNo"
            type="text"
            placeholder="Enter registration number"
            aria-describedby="registrationNo-error"
            defaultValue={state.defaultValues.registrationNo ?? ""}
            required
          />
          <FormError id="registrationNo-error" message={state.errors?.registrationNo} />
        </div>

        <div className="mb-4">
          <Label htmlFor="website">Website</Label>
          <Input
            id="website"
            name="website"
            type="url"
            placeholder="Enter website URL"
            aria-describedby="website-error"
            defaultValue={state.defaultValues.website ?? ""}
            required
          />
          <FormError id="website-error" message={state.errors?.website} />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <Label htmlFor="income">Income</Label>
            <Input
              id="income"
              name="income"
              type="number"
              placeholder="Enter income amount"
              aria-describedby="income-error"
              defaultValue={state.defaultValues.income ?? 0}
              required
            />
            <FormError id="income-error" message={state.errors?.income} />
          </div>
          <div>
            <Label htmlFor="expenditure">Expenditure</Label>
            <Input
              id="expenditure"
              name="expenditure"
              type="number"
              placeholder="Enter expenditure amount"
              aria-describedby="expenditure-error"
              defaultValue={state.defaultValues.expenditure ?? 0}
              required
            />
            <FormError id="expenditure-error" message={state.errors?.expenditure} />
          </div>
        </div>

        <div className="mb-4">
          <Label htmlFor="aimsAndActivities">Aims and Activities</Label>
          <Textarea
            id="aimsAndActivities"
            name="aimsAndActivities"
            placeholder="Enter aims and activities"
            aria-describedby="aimsAndActivities-error"
            defaultValue={state.defaultValues.aimsAndActivities ?? ""}
            required
          />
          <FormError id="aimsAndActivities-error" message={state.errors?.aimsAndActivities} />
        </div>

        <div className="mb-4">
          <Label htmlFor="whatItDoes">What It Does</Label>
          <Textarea
            id="whatItDoes"
            name="whatItDoes"
            placeholder="Enter what the charity does"
            aria-describedby="whatItDoes-error"
            defaultValue={state.defaultValues.whatItDoes ?? ""}
            required
          />
          <FormError id="whatItDoes-error" message={state.errors?.whatItDoes} />
        </div>

        <div className="mb-4">
          <Label htmlFor="whoItHelps">Who It Helps</Label>
          <Textarea
            id="whoItHelps"
            name="whoItHelps"
            placeholder="Enter who the charity helps"
            aria-describedby="whoItHelps-error"
            defaultValue={state.defaultValues.whoItHelps ?? ""}
            required
          />
          <FormError id="whoItHelps-error" message={state.errors?.whoItHelps} />
        </div>

        <div className="mb-4">
          <Label htmlFor="howItWorks">How It Works</Label>
          <Textarea
            id="howItWorks"
            name="howItWorks"
            placeholder="Enter how the charity works"
            aria-describedby="howItWorks-error"
            defaultValue={state.defaultValues.howItWorks ?? ""}
            required
          />
          <FormError id="howItWorks-error" message={state.errors?.howItWorks} />
        </div>
      </FormBody>
      <div className="mt-6 flex justify-end gap-4">
        <Link to="/admin/charities">
          <Button type="reset" disabled={isPending}>
            Cancel
          </Button>
        </Link>
        <Button
          type="submit"
          disabled={isPending}
          loading={isPending}
          className="min-w-[90px]"
        >
          Save Changes
        </Button>
      </div>
    </form>
  );
}
